import type { QueryFunctionContext } from '@tanstack/react-query'
import type { GraphQLResult } from 'aws-amplify/api'
import type { Product } from '@skop-app/lib-domain/types'
import type { SearchProductInput } from '@skop-app/lib-search'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  productsByMasterOrganizationQuery,
  productsByOrganizationProgressQuery,
  productsByOrganizationQuery,
  productsByOrganizationTypeQuery,
  productsByProjectProgressQuery,
  productsByProjectQuery,
  productsByProjectTypeQuery,
  productsByUserQuery,
} from '@/services/graphql'
import {
  getProductStatusesScope,
  type ProductStatusesQueryKey,
} from '@/services/product/productStatuses.cache'

export const PRODUCT_STATUSES_PAGE_SIZE = 100

const client = getAmplifyClient()
const PRODUCT_STATUS_COMPOSITE_PAGE_TOKEN_PREFIX =
  '__productStatusesComposite__:'
const PRODUCT_STATUS_SORT_DIRECTION = 'DESC'

const UNSUPPORTED_SEARCH_OPERATORS = new Set([
  'eqs',
  'match',
  'matchPhrase',
  'matchPhrasePrefix',
  'multiMatch',
  'wildcard',
  'regexp',
])

const SUPPORTED_SCALAR_FILTER_OPERATORS = new Set([
  'eq',
  'ne',
  'exists',
  'attributeExists',
])

const SUPPORTED_CREATED_AT_OPERATORS = new Set([
  'eq',
  'ge',
  'gt',
  'le',
  'lt',
  'between',
])

const SUPPORTED_UPDATED_AT_OPERATORS = new Set([
  'eq',
  'ge',
  'gt',
  'le',
  'lt',
  'between',
])

type ModelStringKeyConditionInput = {
  eq?: string
  ge?: string
  gt?: string
  le?: string
  lt?: string
  between?: string[]
}

type ModelProductStatusFilterInput = {
  [key: string]: unknown
  and?: ModelProductStatusFilterInput[]
  or?: ModelProductStatusFilterInput[]
  not?: ModelProductStatusFilterInput
}

type ProductStatusListPage = {
  items: Product[]
  nextToken: string | null
  total: number
}

export type ProductStatusListScope =
  | { kind: 'project'; value: string }
  | { kind: 'organization'; value: string }
  | { kind: 'master'; value: string }
  | { kind: 'user'; value: string }

type ProductStatusesCompositePageToken = {
  source: 'organization' | 'master'
  organizationToken?: string | null
  masterToken?: string | null
}

type ProductStatusesSingleQueryConfig = {
  field:
    | 'productsByUser'
    | 'productsByOrganization'
    | 'productsByOrganizationProgress'
    | 'productsByOrganizationType'
    | 'productsByMasterOrganization'
    | 'productsByProject'
    | 'productsByProjectProgress'
    | 'productsByProjectType'
  filter?: ModelProductStatusFilterInput
  keyCondition?: ModelStringKeyConditionInput
  keyConditionField: 'createdAt' | 'progress' | 'type'
  nextToken?: string | null
  partitionKey:
    | 'byUserID'
    | 'organizationID'
    | 'organizationMasterID'
    | 'projectID'
  partitionValue: string
}

type ProductStatusesIndexRouteMode =
  | 'legacy'
  | 'inventory-progress'
  | 'inventory-type'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const dedupeProductsById = (products: readonly Product[]): Product[] => [
  ...new Map(
    products.map((product) => [product.id, product] as const),
  ).values(),
]

const normalizePageSize = (pageSize: unknown): number =>
  typeof pageSize === 'number' && Number.isFinite(pageSize) && pageSize > 0
    ? Math.floor(pageSize)
    : PRODUCT_STATUSES_PAGE_SIZE

export const serializeProductStatusesCompositePageToken = (
  token: ProductStatusesCompositePageToken,
): string =>
  `${PRODUCT_STATUS_COMPOSITE_PAGE_TOKEN_PREFIX}${JSON.stringify(token)}`

export const parseProductStatusesCompositePageToken = (
  token: string | null | undefined,
): ProductStatusesCompositePageToken | null => {
  if (
    typeof token !== 'string' ||
    !token.startsWith(PRODUCT_STATUS_COMPOSITE_PAGE_TOKEN_PREFIX)
  ) {
    return null
  }

  try {
    const parsed = JSON.parse(
      token.slice(PRODUCT_STATUS_COMPOSITE_PAGE_TOKEN_PREFIX.length),
    ) as ProductStatusesCompositePageToken

    return parsed?.source === 'organization' || parsed?.source === 'master'
      ? parsed
      : null
  } catch {
    return null
  }
}

const assertSupportedScalarFilter = (
  field: string,
  value: Record<string, unknown>,
) => {
  const supportedOperators =
    field === 'createdAt'
      ? SUPPORTED_CREATED_AT_OPERATORS
      : field === 'updatedAt'
        ? SUPPORTED_UPDATED_AT_OPERATORS
        : SUPPORTED_SCALAR_FILTER_OPERATORS

  Object.keys(value).forEach((operator) => {
    if (UNSUPPORTED_SEARCH_OPERATORS.has(operator)) {
      throw new Error(
        `Unsupported search operator "${operator}" for list product filter "${field}"`,
      )
    }

    if (!supportedOperators.has(operator)) {
      throw new Error(
        `Unsupported list filter operator "${operator}" for "${field}"`,
      )
    }
  })
}

const translateScalarFilter = (
  field: string,
  value: Record<string, unknown>,
): Record<string, unknown> => {
  assertSupportedScalarFilter(field, value)

  const translatedEntries = Object.entries(value).flatMap(([operator, raw]) => {
    if (operator === 'exists') {
      return [['attributeExists', raw]]
    }

    return [[operator, raw]]
  })

  return Object.fromEntries(translatedEntries)
}

const translateFilterValue = (
  field: string,
  value: unknown,
): ModelProductStatusFilterInput[keyof ModelProductStatusFilterInput] => {
  if (!isRecord(value)) {
    throw new Error(`Invalid list product filter payload for "${field}"`)
  }

  if (field === 'quantity' || field === 'dimension') {
    throw new Error(`Unsupported list product filter field "${field}"`)
  }

  return translateScalarFilter(field, value)
}

const isTopLevelEqIdFilter = (
  value: SearchProductInput[keyof SearchProductInput] | undefined,
): value is { eq: string } =>
  isRecord(value) &&
  typeof value.eq === 'string' &&
  Object.keys(value).every((operator) => operator === 'eq')

const validateListProductFilter = (
  filter: SearchProductInput | undefined,
  depth = 0,
) => {
  if (!filter) return

  Object.entries(filter).forEach(([field, value]) => {
    if (value === undefined || value === null) return

    if (field === 'and' || field === 'or') {
      if (!Array.isArray(value)) {
        throw new Error(`Invalid logical list product filter "${field}"`)
      }

      value.forEach((entry) =>
        validateListProductFilter(entry as SearchProductInput, depth + 1),
      )
      return
    }

    if (field === 'not') {
      validateListProductFilter(value as SearchProductInput, depth + 1)
      return
    }

    if (!isRecord(value)) {
      throw new Error(`Invalid list product filter payload for "${field}"`)
    }

    if (field === 'shopID') {
      throw new Error(
        'Unsupported list product scope filter "shopID" on the public list product path',
      )
    }

    if (field === 'projectID') {
      if (depth !== 0 || !isTopLevelEqIdFilter(value)) {
        throw new Error(
          'Unsupported list product scope filter "projectID"; only top-level eq is allowed',
        )
      }

      return
    }

    if (field === 'createdAt' && depth !== 0) {
      throw new Error(
        'Unsupported list product filter "createdAt"; it must be declared at the top level',
      )
    }

    assertSupportedScalarFilter(field, value)
  })
}

export const toModelProductStatusFilterInput = (
  filter: SearchProductInput | undefined,
): ModelProductStatusFilterInput | undefined => {
  if (!filter) return undefined

  const translatedEntries = Object.entries(filter).flatMap(([field, value]) => {
    if (value === undefined || value === null) return []

    if (field === 'and' || field === 'or') {
      if (!Array.isArray(value)) {
        throw new Error(`Invalid logical list product filter "${field}"`)
      }

      const translated = value
        .filter((entry): entry is SearchProductInput => Boolean(entry))
        .map((entry) => toModelProductStatusFilterInput(entry))
        .filter(
          (
            entry,
          ): entry is NonNullable<
            ReturnType<typeof toModelProductStatusFilterInput>
          > => Boolean(entry),
        )

      return translated.length > 0 ? [[field, translated]] : []
    }

    if (field === 'not') {
      const translated = toModelProductStatusFilterInput(
        value as SearchProductInput,
      )

      return translated ? [[field, translated]] : []
    }

    return [[field, translateFilterValue(field, value)]]
  })

  return translatedEntries.length > 0
    ? (Object.fromEntries(translatedEntries) as ModelProductStatusFilterInput)
    : undefined
}

const normalizeCreatedAtKeyCondition = (
  createdAt: unknown,
): ModelStringKeyConditionInput | undefined => {
  if (!isRecord(createdAt)) return undefined

  assertSupportedScalarFilter('createdAt', createdAt)

  const allowedOperators = ['eq', 'ge', 'gt', 'le', 'lt', 'between'] as const
  const keyConditionEntries = allowedOperators.flatMap((operator) => {
    const value = createdAt[operator]
    return value === undefined ? [] : [[operator, value]]
  })

  return keyConditionEntries.length > 0
    ? (Object.fromEntries(keyConditionEntries) as ModelStringKeyConditionInput)
    : undefined
}

const omitTopLevelFilterKeys = (
  filter: SearchProductInput | undefined,
  keys: string[],
): SearchProductInput | undefined => {
  if (!filter) return undefined

  const nextEntries = Object.entries(filter).filter(
    ([key]) => !keys.includes(key),
  )
  return nextEntries.length > 0
    ? (Object.fromEntries(nextEntries) as SearchProductInput)
    : undefined
}

const mergeFiltersWithAnd = (
  baseFilter: ModelProductStatusFilterInput | undefined,
  extraFilter: ModelProductStatusFilterInput | undefined,
): ModelProductStatusFilterInput | undefined => {
  if (baseFilter && extraFilter) {
    return {
      and: [baseFilter, extraFilter],
    }
  }

  return baseFilter ?? extraFilter
}

const getEqStringFilterValue = (
  filterValue: SearchProductInput[keyof SearchProductInput] | undefined,
): string | undefined => {
  if (!isRecord(filterValue) || typeof filterValue.eq !== 'string')
    return undefined
  return filterValue.eq
}

const resolveProductStatusesRouteMode = ({
  filters,
  queryKey,
}: {
  filters: SearchProductInput | undefined
  queryKey:
    | ProductStatusesQueryKey
    | QueryFunctionContext<ProductStatusesQueryKey, string | null>['queryKey']
}): ProductStatusesIndexRouteMode => {
  const { routeHint } = getProductStatusesScope(queryKey)
  if (!routeHint || isRecord(filters?.createdAt)) {
    return 'legacy'
  }

  const scope = resolveProductStatusesListScope({ filters, queryKey })
  if (scope?.kind !== 'organization' && scope?.kind !== 'project') {
    return 'legacy'
  }

  if (
    routeHint === 'inventory-active' &&
    getEqStringFilterValue(filters?.progress) === 'active'
  ) {
    return 'inventory-progress'
  }

  if (
    routeHint === 'inventory-removed' &&
    getEqStringFilterValue(filters?.type) === 'done'
  ) {
    return 'inventory-type'
  }

  return 'legacy'
}

export const resolveProductStatusesListScope = ({
  filters,
  queryKey,
}: {
  filters: SearchProductInput | undefined
  queryKey:
    | ProductStatusesQueryKey
    | QueryFunctionContext<ProductStatusesQueryKey, string | null>['queryKey']
}): ProductStatusListScope | null => {
  const {
    userID,
    organizationID,
    isOrganizationView = false,
    isMaster = false,
  } = getProductStatusesScope(queryKey)

  const projectID = getEqStringFilterValue(filters?.projectID)
  if (projectID) {
    return { kind: 'project', value: projectID }
  }

  if (organizationID && isOrganizationView && !isMaster) {
    return null
  }

  if (organizationID && isOrganizationView && isMaster) {
    return { kind: 'master', value: organizationID }
  }

  if (organizationID && !isMaster) {
    return { kind: 'organization', value: organizationID }
  }

  if (organizationID && isMaster) {
    return { kind: 'master', value: organizationID }
  }

  if (userID) {
    return { kind: 'user', value: userID }
  }

  return null
}

const buildProductStatusesSingleQueryConfig = (params: {
  filter?: ModelProductStatusFilterInput
  nextToken?: string | null
  routeMode: ProductStatusesIndexRouteMode
  scope: ProductStatusListScope
  createdAt?: ModelStringKeyConditionInput
}): ProductStatusesSingleQueryConfig => {
  const { createdAt, filter, nextToken, routeMode, scope } = params
  const resolvedRouteMode =
    scope.kind === 'organization' || scope.kind === 'project'
      ? routeMode
      : 'legacy'

  switch (scope.kind) {
    case 'user':
      return {
        field: 'productsByUser',
        partitionKey: 'byUserID',
        partitionValue: scope.value,
        keyCondition: createdAt,
        keyConditionField: 'createdAt',
        filter,
        nextToken,
      }
    case 'organization':
      return {
        field:
          resolvedRouteMode === 'inventory-progress'
            ? 'productsByOrganizationProgress'
            : resolvedRouteMode === 'inventory-type'
              ? 'productsByOrganizationType'
              : 'productsByOrganization',
        partitionKey: 'organizationID',
        partitionValue: scope.value,
        keyCondition:
          resolvedRouteMode === 'inventory-progress'
            ? { eq: 'active' }
            : resolvedRouteMode === 'inventory-type'
              ? { eq: 'done' }
              : createdAt,
        keyConditionField:
          resolvedRouteMode === 'inventory-progress'
            ? 'progress'
            : resolvedRouteMode === 'inventory-type'
              ? 'type'
              : 'createdAt',
        filter,
        nextToken,
      }
    case 'master':
      return {
        field: 'productsByMasterOrganization',
        partitionKey: 'organizationMasterID',
        partitionValue: scope.value,
        keyCondition: createdAt,
        keyConditionField: 'createdAt',
        filter,
        nextToken,
      }
    case 'project':
      return {
        field:
          resolvedRouteMode === 'inventory-progress'
            ? 'productsByProjectProgress'
            : resolvedRouteMode === 'inventory-type'
              ? 'productsByProjectType'
              : 'productsByProject',
        partitionKey: 'projectID',
        partitionValue: scope.value,
        keyCondition:
          resolvedRouteMode === 'inventory-progress'
            ? { eq: 'active' }
            : resolvedRouteMode === 'inventory-type'
              ? { eq: 'done' }
              : createdAt,
        keyConditionField:
          resolvedRouteMode === 'inventory-progress'
            ? 'progress'
            : resolvedRouteMode === 'inventory-type'
              ? 'type'
              : 'createdAt',
        filter,
        nextToken,
      }
  }
}

const executeProductStatusesSingleScopeQuery = async (params: {
  createdAt?: ModelStringKeyConditionInput
  filter?: ModelProductStatusFilterInput
  limit: number
  nextToken?: string | null
  routeMode: ProductStatusesIndexRouteMode
  scope: ProductStatusListScope
}): Promise<{ items: Product[]; nextToken: string | null }> => {
  const { createdAt, filter, limit, nextToken, routeMode, scope } = params
  const queryConfig = buildProductStatusesSingleQueryConfig({
    createdAt,
    scope,
    filter,
    nextToken,
    routeMode,
  })

  const query =
    queryConfig.field === 'productsByUser'
      ? productsByUserQuery
      : queryConfig.field === 'productsByOrganization'
        ? productsByOrganizationQuery
        : queryConfig.field === 'productsByOrganizationProgress'
          ? productsByOrganizationProgressQuery
          : queryConfig.field === 'productsByOrganizationType'
            ? productsByOrganizationTypeQuery
            : queryConfig.field === 'productsByMasterOrganization'
              ? productsByMasterOrganizationQuery
              : queryConfig.field === 'productsByProject'
                ? productsByProjectQuery
                : queryConfig.field === 'productsByProjectProgress'
                  ? productsByProjectProgressQuery
                  : productsByProjectTypeQuery

  const { data } = (await client.graphql({
    query,
    variables: {
      [queryConfig.partitionKey]: queryConfig.partitionValue,
      [queryConfig.keyConditionField]: queryConfig.keyCondition,
      sortDirection: PRODUCT_STATUS_SORT_DIRECTION,
      filter: queryConfig.filter,
      limit,
      nextToken: queryConfig.nextToken ?? undefined,
    },
  })) as GraphQLResult<
    Record<string, { items?: Product[]; nextToken?: string | null }>
  >

  const connection = data?.[queryConfig.field]
  return {
    items: dedupeProductsById(connection?.items ?? []),
    nextToken: connection?.nextToken ?? null,
  }
}

const buildResidualListFilter = (params: {
  filters: SearchProductInput | undefined
  omittedTopLevelKeys: string[]
}): ModelProductStatusFilterInput | undefined =>
  toModelProductStatusFilterInput(
    omitTopLevelFilterKeys(params.filters, params.omittedTopLevelKeys),
  )

const listSingleScopedProductStatuses = async (params: {
  filters: SearchProductInput | undefined
  pageParam: string | null
  pageSize: number
  routeMode: ProductStatusesIndexRouteMode
  scope: ProductStatusListScope
  userOnlyWithoutOrganization?: boolean
  personalWorkspaceOrganizationID?: string
}): Promise<ProductStatusListPage> => {
  const {
    filters,
    pageParam,
    pageSize,
    routeMode,
    scope,
    userOnlyWithoutOrganization = false,
    personalWorkspaceOrganizationID,
  } = params
  const resolvedRouteMode =
    scope.kind === 'organization' || scope.kind === 'project'
      ? routeMode
      : 'legacy'

  const createdAt =
    resolvedRouteMode === 'legacy'
      ? normalizeCreatedAtKeyCondition(filters?.createdAt)
      : undefined
  const omittedTopLevelKeys = [
    ...(resolvedRouteMode === 'legacy'
      ? (['createdAt'] as const)
      : resolvedRouteMode === 'inventory-progress'
        ? (['progress'] as const)
        : (['type'] as const)),
    scope.kind === 'user'
      ? 'byUserID'
      : scope.kind === 'organization'
        ? 'organizationID'
        : scope.kind === 'master'
          ? 'organizationMasterID'
          : 'projectID',
  ]

  const baseFilter = buildResidualListFilter({
    filters,
    omittedTopLevelKeys,
  })

  const personalOrganizationFilter: ModelProductStatusFilterInput =
    personalWorkspaceOrganizationID
      ? {
          or: [
            { organizationID: { attributeExists: false } },
            { organizationID: { eq: personalWorkspaceOrganizationID } },
          ],
        }
      : { organizationID: { attributeExists: false } }
  const scopeFilter =
    scope.kind === 'user' && userOnlyWithoutOrganization
      ? mergeFiltersWithAnd(baseFilter, personalOrganizationFilter)
      : baseFilter

  const page = await executeProductStatusesSingleScopeQuery({
    scope,
    filter: scopeFilter,
    createdAt,
    limit: pageSize,
    nextToken: pageParam,
    routeMode: resolvedRouteMode,
  })

  return {
    items: page.items,
    total: 0,
    nextToken: page.nextToken,
  }
}

const listMasterScopedProductStatuses = async (params: {
  createdAt?: ModelStringKeyConditionInput
  filters: SearchProductInput | undefined
  organizationID: string
  pageParam: string | null
  pageSize: number
}): Promise<ProductStatusListPage> => {
  const { createdAt, filters, organizationID, pageParam, pageSize } = params
  const parsedToken = parseProductStatusesCompositePageToken(pageParam)
  const currentSource = parsedToken?.source ?? 'organization'

  const organizationFilter = buildResidualListFilter({
    filters,
    omittedTopLevelKeys: [
      'createdAt',
      'organizationID',
      'organizationMasterID',
    ],
  })

  const masterFilter = mergeFiltersWithAnd(organizationFilter, {
    organizationID: {
      ne: organizationID,
    },
  })

  if (currentSource === 'organization') {
    const organizationPage = await executeProductStatusesSingleScopeQuery({
      scope: { kind: 'organization', value: organizationID },
      filter: organizationFilter,
      createdAt,
      limit: pageSize,
      nextToken: parsedToken?.organizationToken ?? null,
      routeMode: 'legacy',
    })

    if (!organizationPage.nextToken && organizationPage.items.length === 0) {
      const masterPage = await executeProductStatusesSingleScopeQuery({
        scope: { kind: 'master', value: organizationID },
        filter: masterFilter,
        createdAt,
        limit: pageSize,
        nextToken: parsedToken?.masterToken ?? null,
        routeMode: 'legacy',
      })

      return {
        items: masterPage.items,
        nextToken: masterPage.nextToken
          ? serializeProductStatusesCompositePageToken({
              source: 'master',
              organizationToken: null,
              masterToken: masterPage.nextToken,
            })
          : null,
        total: 0,
      }
    }

    const nextToken = organizationPage.nextToken
      ? serializeProductStatusesCompositePageToken({
          source: 'organization',
          organizationToken: organizationPage.nextToken,
          masterToken: parsedToken?.masterToken ?? null,
        })
      : serializeProductStatusesCompositePageToken({
          source: 'master',
          organizationToken: null,
          masterToken: parsedToken?.masterToken ?? null,
        })

    return {
      items: organizationPage.items,
      nextToken,
      total: 0,
    }
  }

  const masterPage = await executeProductStatusesSingleScopeQuery({
    scope: { kind: 'master', value: organizationID },
    filter: masterFilter,
    createdAt,
    limit: pageSize,
    nextToken: parsedToken?.masterToken ?? null,
    routeMode: 'legacy',
  })

  return {
    items: masterPage.items,
    nextToken: masterPage.nextToken
      ? serializeProductStatusesCompositePageToken({
          source: 'master',
          organizationToken: null,
          masterToken: masterPage.nextToken,
        })
      : null,
    total: 0,
  }
}

export const listScopedProductStatuses = async ({
  props,
  filters,
}: {
  props: QueryFunctionContext<ProductStatusesQueryKey, string | null>
  filters: SearchProductInput | undefined
}): Promise<ProductStatusListPage> => {
  const { queryKey, pageParam } = props
  validateListProductFilter(filters)
  const route = resolveProductStatusesListScope({ filters, queryKey })
  const queryScope = getProductStatusesScope(queryKey)
  const { pageSize } = queryScope
  const resolvedPageSize = normalizePageSize(pageSize)
  const routeMode = resolveProductStatusesRouteMode({ filters, queryKey })

  if (!route) {
    return {
      items: [],
      total: 0,
      nextToken: null,
    }
  }

  const createdAt = normalizeCreatedAtKeyCondition(filters?.createdAt)
  const runList = (effectiveRouteMode: ProductStatusesIndexRouteMode) => {
    if (route.kind === 'master' && !queryScope.isOrganizationView) {
      return listMasterScopedProductStatuses({
        organizationID: route.value,
        filters,
        createdAt: effectiveRouteMode === 'legacy' ? createdAt : undefined,
        pageParam,
        pageSize: resolvedPageSize,
      })
    }

    return listSingleScopedProductStatuses({
      filters,
      scope: route,
      pageParam,
      pageSize: resolvedPageSize,
      routeMode: effectiveRouteMode,
      userOnlyWithoutOrganization: route.kind === 'user',
      personalWorkspaceOrganizationID:
        route.kind === 'user' &&
        queryScope.scopeKind === 'solo' &&
        !queryScope.isOrganizationView
          ? queryScope.personalWorkspaceOrganizationID
          : undefined,
    })
  }

  if (routeMode === 'legacy') {
    return runList('legacy')
  }

  try {
    return await runList(routeMode)
  } catch (error) {
    console.warn('Falling back to legacy ProductStatus list routing', error)
    return runList('legacy')
  }
}

export const listAllProductStatusesForScope = async (params: {
  filters?: SearchProductInput
  pageSize?: number
  routeMode?: ProductStatusesIndexRouteMode
  scope: ProductStatusListScope
  useMasterUnion?: boolean
  userOnlyWithoutOrganization?: boolean
}): Promise<Product[]> => {
  const {
    filters,
    pageSize,
    routeMode = 'legacy',
    scope,
    useMasterUnion = false,
    userOnlyWithoutOrganization = false,
  } = params
  validateListProductFilter(filters)

  const items: Product[] = []
  let nextToken: string | null = null

  do {
    const page =
      scope.kind === 'master' && useMasterUnion
        ? await listMasterScopedProductStatuses({
            organizationID: scope.value,
            filters,
            createdAt:
              routeMode === 'legacy'
                ? normalizeCreatedAtKeyCondition(filters?.createdAt)
                : undefined,
            pageParam: nextToken,
            pageSize: normalizePageSize(pageSize),
          })
        : await listSingleScopedProductStatuses({
            filters,
            scope,
            pageParam: nextToken,
            pageSize: normalizePageSize(pageSize),
            routeMode,
            userOnlyWithoutOrganization,
          })

    items.push(...page.items)
    nextToken = page.nextToken
  } while (nextToken)

  return dedupeProductsById(items)
}
